import React from 'react';
import Icon from '../../../components/AppIcon';

const WellnessCenterTabs = ({ center = {}, activeTab = 'facilities', onTabChange }) => {
  const tabs = [
    { id: 'facilities', label: 'Facilities', icon: 'Building' },
    { id: 'programs', label: 'Wellness Programs', icon: 'Heart' },
    { id: 'ees-technology', label: 'EES Technology', icon: 'Zap' },
    { id: 'location', label: 'Location & Amenities', icon: 'MapPin' },
    { id: 'reviews', label: 'Reviews', icon: 'Star' },
  ];

  const renderFacilities = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">Center Facilities</h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {center?.facilities?.map((facility, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-secondary-50 rounded-lg">
              <Icon name="Check" size={16} className="text-success" />
              <span className="text-text-primary">{facility}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div>
        <h4 className="text-md font-medium text-text-primary mb-3">EES Chamber Details</h4>
        <div className="bg-primary-50 p-4 rounded-lg border border-primary-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-primary">Available Units:</span>
              <span className="ml-2 text-text-primary">{center?.eesUnits} EES Systems</span>
            </div>
            <div>
              <span className="text-sm font-medium text-primary">Chamber Capacity:</span>
              <span className="ml-2 text-text-primary">Up to {center?.capacity} guests</span>
            </div>
            <div>
              <span className="text-sm font-medium text-primary">Session Length:</span>
              <span className="ml-2 text-text-primary">2-4 hours recommended</span>
            </div>
            <div>
              <span className="text-sm font-medium text-primary">Temperature Control:</span>
              <span className="ml-2 text-text-primary">Climate controlled</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPrograms = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">Available Wellness Programs</h3>
        <div className="space-y-4">
          {center?.wellnessPrograms?.map((program, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-md font-medium text-text-primary">{program.name}</h4>
                <span className="text-lg font-bold text-primary">{program.price}</span>
              </div>
              <p className="text-text-secondary text-sm mb-3">{program.description}</p>
              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} />
                  <span>{program.duration}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Users" size={14} />
                  <span>{program.groupSize}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} />
                  <span>{program.frequency}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEESTechnology = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">Energy Enhancement System Technology</h3>
        <div className="bg-accent-50 p-6 rounded-lg border border-accent-100 mb-6">
          <h4 className="text-md font-medium text-accent-600 mb-3">How EES Works</h4>
          <p className="text-text-secondary mb-4">
            The Energy Enhancement System (EES) generates multiple bio-active energy fields, including scalar waves, 
            which can allow cell regeneration, improve immune function, provide relief from pain, detoxify the body, 
            elevate moods, and assist in balancing right and left hemispheres of the brain.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h5 className="font-medium text-text-primary">Benefits Include:</h5>
              <ul className="space-y-1 text-sm text-text-secondary">
                <li className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success" />
                  <span>Enhanced cellular regeneration</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success" />
                  <span>Improved immune system function</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success" />
                  <span>Natural pain relief</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success" />
                  <span>Detoxification support</span>
                </li>
              </ul>
            </div>
            <div className="space-y-2">
              <h5 className="font-medium text-text-primary">Technology Features:</h5>
              <ul className="space-y-1 text-sm text-text-secondary">
                <li className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-primary" />
                  <span>Scalar wave technology</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-primary" />
                  <span>Bio-active energy fields</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-primary" />
                  <span>Frequency optimization</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-primary" />
                  <span>Harmonic resonance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLocation = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">Location & Nearby Amenities</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-md font-medium text-text-primary mb-3">Wellness Scores</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Walk Score</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 h-2 bg-secondary-200 rounded-full">
                    <div className="w-3/4 h-2 bg-success rounded-full"></div>
                  </div>
                  <span className="font-medium text-text-primary">85/100</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Transit Score</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 h-2 bg-secondary-200 rounded-full">
                    <div className="w-4/5 h-2 bg-primary rounded-full"></div>
                  </div>
                  <span className="font-medium text-text-primary">90/100</span>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-md font-medium text-text-primary mb-3">Nearby Places</h4>
            <div className="space-y-2">
              {center?.nearbyPlaces?.map((place, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={14} className="text-accent" />
                    <span className="text-text-primary">{place.name}</span>
                  </div>
                  <span className="text-text-secondary">{place.distance}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderReviews = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">Guest Reviews</h3>
        <div className="space-y-4">
          {center?.reviews?.map((review, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {review.guestName?.split(' ').map(n => n[0]).join('') || 'G'}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-text-primary">{review.guestName}</h4>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={14}
                            className={i < review.rating ? 'text-warning fill-current' : 'text-secondary-300'}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-text-secondary">{review.date}</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-text-secondary">{review.comment}</p>
              {review.experience && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {review.experience.map((exp, expIndex) => (
                    <span
                      key={expIndex}
                      className="inline-block px-2 py-1 bg-primary-100 text-primary text-xs rounded-full"
                    >
                      {exp}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'facilities':
        return renderFacilities();
      case 'programs':
        return renderPrograms();
      case 'ees-technology':
        return renderEESTechnology();
      case 'location':
        return renderLocation();
      case 'reviews':
        return renderReviews();
      default:
        return renderFacilities();
    }
  };

  return (
    <div className="bg-surface rounded-lg shadow-elevation-1 overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-all duration-200 border-b-2 ${
                activeTab === tab.id
                  ? 'border-primary text-primary bg-primary-50' :'border-transparent text-text-secondary hover:text-text-primary hover:bg-secondary-50'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default WellnessCenterTabs;